import React, { useCallback, useMemo, useRef, useState } from 'react';
import styles from './select.module.scss';
import { SelectOption } from '../../types';
import ChevronIcon from '../../assets/icons/chevron_down.svg?react';
import { useOnClickOutside } from '../../hooks/useClickOutside';
import { TextInput } from '../text-input/TextInput';
import Spinner from "../spinner/Spinner";

type Props = {
  label?: string;
  renderLabel?: (labelComponent: React.ReactNode) => React.ReactNode;
  options: SelectOption[];
  onChange?: (value: SelectOption) => void;
  disabled?: boolean;
  inDropDown?: boolean;
  placeholder?: string;
  value?: string | null;
  className?: string;
  inline?: boolean;
  subTitle?: string;
  error?: boolean;
  showSearch?: boolean;
  searchAutoFocus?: boolean;
  loading?: boolean;
  doNotSort?: boolean;
  menuClassName?: string;
  color?: string;
};

function Select(props: Props) {
  const {
    className,
    label,
    placeholder = 'Select an option',
    options,
    value,
    onChange,
    inDropDown,
    disabled,
    renderLabel,
    inline,
    subTitle,
    error,
    showSearch = true,
    searchAutoFocus,
    loading,
    doNotSort,
    menuClassName,
    color,
  } = props;
  const [opened, setOpened] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState('');

  useOnClickOutside(wrapperRef, () => {
    setOpened(false);
  });

  const wrapperClasses = useMemo(() => {
    const classes = [styles.wrapper, className];
    if (inline) {
      classes.push(styles.inline);
    }
    if (opened) {
      classes.push(styles.opened);
    }
    if (error) {
      classes.push(styles.error);
    }
    if (disabled) {
      classes.push(styles.disabled);
    }
    return classes.join(' ').trim();
  }, [opened, className, disabled, inline, error]);

  const selectClasses = useMemo(() => {
    const classes = [styles.select];
    
    if (color) {
      classes.push(styles[color]);
    }
    return classes.join(' ').trim();
  }, [color]);

  const placeholderClasses = useMemo(() => {
    const classes = [styles.placeholder];
    
    if (color) {
      classes.push(styles.whiteColor);
    }
    return classes.join(' ').trim();
  }, [color]);

  const selectTitleClasses = useMemo(() => {
    const classes = [styles.selectedTitle];
    
    if (color) {
      classes.push(styles.whiteColor);
    }
    return classes.join(' ').trim();
  }, [color]);

  const iconClasses = useMemo(() => {
    const classes = [styles.chevron];
    
    if (color) {
      classes.push(styles.fillWhite);
    }
    return classes.join(' ').trim();
  }, [color]);

  const handleToggle = () => {
    if (disabled) return;
    setOpened(!opened);
  };
  const handleChange = useCallback(option => () => {
    onChange && onChange(option);
  }, [onChange]);

  const selectedValue = useMemo(() => {
    if (!value) return null;
    // TO DO: currently, we have bad datas for the style options in the DB. We have to udpate the style manually at the edit-lesson
    // After DB update, we should revert to the prev version that is commented out.
    return options.find(o => (`${o.value}` === `${value}` || o.oldValues?.includes(value)));
    // return options.find(o => `${o.value}` === `${value}`);
  }, [options, value]);

  const handleChangeSearch = useCallback((value) => {
    setSearch(value);
  }, []);

  const handleStop = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const sorted = useMemo(() => {
    if (!options) return [];
    if (!!doNotSort) return options;
    return [...options].sort((a, b) => a.label.localeCompare(b.label));
  }, [options, doNotSort]);

  const filtered = useMemo(() => {
    if (!search) return sorted;
    const s = search.toLowerCase();
    return sorted.filter(o => o.label.toLowerCase().includes(s));
  }, [sorted, search]);

  return (
    <div className={wrapperClasses} ref={wrapperRef} onClick={handleToggle}>
      {(renderLabel || label) ? (
        <div className={styles.header}>
          {typeof renderLabel === 'function' ? renderLabel(<div className={styles.headerLabel}>{label}</div>) :
            (label ? <div className={styles.headerLabel}>{label}</div> : null)}
        </div>
      ) : null}
      <div className={styles.selectWrapper}>
        <div className={selectClasses}>
          {loading ? <div style={{marginLeft: 10}}><Spinner width={20} height={20}/></div> : selectedValue ?
            <div className={selectTitleClasses}>{selectedValue.label}</div> :
            <div className={placeholderClasses}>
              {placeholder}
            </div>}
          <div className={styles.chevronWrapper}>
            <ChevronIcon className={iconClasses}/>
          </div>
        </div>
        {subTitle && <div className={styles.subtitle}>{subTitle}</div>}
        {opened &&
        <ul className={`${styles.list}${menuClassName ? ` ${menuClassName}` : ''}`}>
          {showSearch && <li className={styles.search} onClick={handleStop}>
           <TextInput allowSpecialCharacters autoFocus={searchAutoFocus} placeholder={'Search'} label={''} id={'search'} value={search}
                      onChange={handleChangeSearch} inDropDown={inDropDown}/>
          </li>}
          {filtered.length ?
            filtered.map(o => {
              return (
                <li key={o.value} onClick={handleChange(o)} className={`${styles.listItem} ${selectedValue?.label === o.label ? styles.selectedItem : ''}`}>{o.label}</li>
              );
            })
            : <li className={styles.listItem}>No options</li>
          }
        </ul>
        }
      </div>
    </div>
  );
}

export default Select;
